import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

interface PathPoint {
  x: number;
  y: number;
}

interface Circuit {
  path: PathPoint[];
  progress: number;
  speed: number;
  opacity: number;
  fadeOut: boolean;
  startNodeOpacity: number;
  endNodeOpacity: number;
  lineOpacity: number;
  nodeProgress: number;
}

const CircuitAnimation: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const circuitsRef = useRef<Circuit[]>([]);
  const animationFrameRef = useRef<number>();

  const createPath = (startX: number, startY: number, length: number): PathPoint[] => {
    const path: PathPoint[] = [{ x: startX, y: startY }];
    let currentX = startX;
    let currentY = startY;
    let direction: 'horizontal' | 'vertical' = Math.random() > 0.5 ? 'horizontal' : 'vertical';
    
    for (let i = 0; i < length; i++) {
      if (direction === 'horizontal') {
        currentX += (Math.random() > 0.5 ? 1 : -1) * (100 + Math.random() * 200);
        direction = 'vertical';
      } else {
        currentY += (Math.random() > 0.5 ? 1 : -1) * (100 + Math.random() * 200);
        direction = 'horizontal';
      }
      path.push({ x: currentX, y: currentY });
    }
    
    return path;
  };

  const createCircuit = (width: number, height: number): Circuit => {
    const startX = Math.random() * width;
    const startY = Math.random() * height;
    const pathLength = 5 + Math.floor(Math.random() * 3);
    const lineOpacity = 0.15 + Math.random() * 0.35;

    return {
      path: createPath(startX, startY, pathLength),
      progress: 0,
      speed: 0.0008 + Math.random() * 0.0004,
      opacity: 0.4 + Math.random() * 0.3,
      fadeOut: false,
      startNodeOpacity: lineOpacity,
      endNodeOpacity: lineOpacity,
      lineOpacity: lineOpacity,
      nodeProgress: 0
    };
  };

  const drawCircuit = (
    ctx: CanvasRenderingContext2D,
    circuit: Circuit,
    progress: number
  ) => {
    const { path, fadeOut, lineOpacity, nodeProgress } = circuit;
    
    const fadeOutProgress = fadeOut ? (1.2 - progress) / 0.2 : 1;
    
    // First draw the line
    ctx.strokeStyle = `rgba(107, 70, 193, ${lineOpacity * fadeOutProgress})`;
    ctx.lineWidth = 20;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Draw the path
    ctx.beginPath();
    let lastDrawnPoint: PathPoint | null = null;
    
    for (let i = 0; i < path.length - 1; i++) {
      const start = path[i];
      const end = path[i + 1];
      
      if (i === 0) {
        ctx.moveTo(start.x, start.y);
      }
      
      const segmentLength = Math.hypot(end.x - start.x, end.y - start.y);
      const totalLength = path.reduce((acc, _, idx) => {
        if (idx < path.length - 1) {
          const next = path[idx + 1];
          return acc + Math.hypot(next.x - path[idx].x, next.y - path[idx].y);
        }
        return acc;
      }, 0);
      
      const segmentProgress = (progress * totalLength - path.slice(0, i).reduce((acc, _, idx) => {
        if (idx < i) {
          const next = path[idx + 1];
          return acc + Math.hypot(next.x - path[idx].x, next.y - path[idx].y);
        }
        return acc;
      }, 0)) / segmentLength;
      
      if (segmentProgress <= 0) break;
      
      if (segmentProgress >= 1) {
        ctx.lineTo(end.x, end.y);
        lastDrawnPoint = end;
      } else {
        const currentX = start.x + (end.x - start.x) * segmentProgress;
        const currentY = start.y + (end.y - start.y) * segmentProgress;
        ctx.lineTo(currentX, currentY);
        lastDrawnPoint = { x: currentX, y: currentY };
        break;
      }
    }
    ctx.stroke();

    // Then draw the circles on top with solid fill and faster fade-in
    if (progress > 0) {
      const startNodeFade = Math.min(nodeProgress * 4, 1);
      // Draw solid background circle first
      ctx.fillStyle = `rgba(0, 0, 0, ${fadeOutProgress * startNodeFade})`;
      ctx.beginPath();
      ctx.arc(path[0].x, path[0].y, 27, 0, Math.PI * 2);
      ctx.fill();
      // Then draw the purple circle
      ctx.fillStyle = `rgba(107, 70, 193, ${fadeOutProgress * startNodeFade})`;
      ctx.beginPath();
      ctx.arc(path[0].x, path[0].y, 27, 0, Math.PI * 2);
      ctx.fill();
    }
    
    if (lastDrawnPoint) {
      const endNodeFade = Math.min((nodeProgress - 0.2) * 4, 1);
      // Draw solid background circle first
      ctx.fillStyle = `rgba(0, 0, 0, ${fadeOutProgress * endNodeFade})`;
      ctx.beginPath();
      ctx.arc(lastDrawnPoint.x, lastDrawnPoint.y, 27, 0, Math.PI * 2);
      ctx.fill();
      // Then draw the purple circle
      ctx.fillStyle = `rgba(107, 70, 193, ${fadeOutProgress * endNodeFade})`;
      ctx.beginPath();
      ctx.arc(lastDrawnPoint.x, lastDrawnPoint.y, 27, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  const animate = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');

    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw circuits
      circuitsRef.current.forEach((circuit, index) => {
        circuit.progress += circuit.speed;
        // Increased node progress speed significantly
        circuit.nodeProgress = Math.min(circuit.nodeProgress + circuit.speed * 5, 1);
        
        // Start fade out when progress reaches 1
        if (circuit.progress >= 1 && !circuit.fadeOut) {
          circuit.fadeOut = true;
        }
        
        // Reset circuit when it completes fade out
        if (circuit.progress >= 1.2) {
          circuitsRef.current[index] = createCircuit(canvas.width, canvas.height);
        } else {
          drawCircuit(ctx, circuit, circuit.progress);
        }
      });

      // Draw gradient overlay
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0.8)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    animationFrameRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleResize = () => {
      const { width, height } = canvas.getBoundingClientRect();
      canvas.width = width;
      canvas.height = height;

      // Reduced number of circuits from 15 to 6
      circuitsRef.current = Array(6).fill(null).map(() => 
        createCircuit(width, height)
      );
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
        background: 'linear-gradient(180deg, #000000 0%, #1a1133 100%)',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      />
    </Box>
  );
};

export default CircuitAnimation; 